import React from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${({ theme }) => theme.zIndex.level10};
`;

const ModalContainer = styled.div`
  background-color: ${({ theme }) => theme.white};
  max-width: 700px;
  max-height: 80vh;
  width: 90%;
  overflow-y: auto;
  padding: 40px;
  position: relative;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.borders};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.primary};
    border-radius: 4px;
    transition: background 0.3s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.opacity};
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.primary} ${({ theme }) => theme.borders};

  ${({ theme }) => theme.mqx.tablet} {
    padding: 20px;
    max-height: 90vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 2.4rem;
  cursor: pointer;
  color: ${({ theme }) => theme.black};
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const Title = styled.h2`
  font-size: 2.4rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 30px;
  text-align: center;
  border-bottom: 2px solid ${({ theme }) => theme.primary};
  padding-bottom: 15px;
`;

const Section = styled.section`
  margin-bottom: 25px;
`;

const SectionHeading = styled.h3`
  font-size: 1.6rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.black};
`;

const SectionContent = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContentItem = styled.li`
  font-size: 1.4rem;
  line-height: 1.8;
  letter-spacing: 1px;
  margin-bottom: 8px;
  padding-left: 15px;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.primary};
  }
`;

const privacyPolicy = {
  title: "Polityka prywatności",
  website: "olgalukasik.pl",
  lastUpdated: null,
  sections: [
    {
      id: "intro",
      heading: "Informacje ogólne",
      content: [
        "Serwis olgalukasik.pl szanuje prywatność użytkowników.",
        "Strona nie wykorzystuje plików cookies."
      ]
    },
    {
      id: "data",
      heading: "Dane osobowe",
      content: [
        "Dane osobowe zbierane są wyłącznie poprzez formularz kontaktowy.",
        "Mogą to być: imię, adres e-mail oraz inne informacje podane dobrowolnie w treści wiadomości.",
        "Podanie danych jest dobrowolne, ale niezbędne do udzielenia odpowiedzi."
      ]
    },
    {
      id: "purpose",
      heading: "Cel i podstawa przetwarzania",
      content: [
        "Dane są przetwarzane wyłącznie w celu obsługi zapytań przesłanych przez formularz kontaktowy.",
        "Podstawą prawną jest zgoda użytkownika oraz prawnie uzasadniony interes administratora polegający na prowadzeniu korespondencji."
      ]
    },
    {
      id: "retention",
      heading: "Okres przechowywania",
      content: [
        "Dane są przechowywane tylko przez czas potrzebny do realizacji kontaktu lub do momentu cofnięcia zgody."
      ]
    },
    {
      id: "recipients",
      heading: "Odbiorcy danych",
      content: [
        "Dane nie są przekazywane podmiotom trzecim, poza firmą hostingową obsługującą stronę.",
        "Dane nie są przekazywane poza UE."
      ]
    },
    {
      id: "rights",
      heading: "Prawa użytkownika",
      content: [
        "Masz prawo do dostępu do swoich danych, ich poprawienia, usunięcia, ograniczenia przetwarzania oraz cofnięcia zgody w dowolnym momencie.",
        "W sprawach związanych z danymi osobowymi możesz skontaktować się poprzez formularz na stronie.",
        "Przysługuje Ci również prawo złożenia skargi do Prezesa UODO."
      ]
    },
    {
      id: "changes",
      heading: "Zmiany",
      content: [
        "Polityka prywatności może być aktualizowana. Aktualna wersja jest zawsze dostępna na stronie."
      ]
    }
  ]
};

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CloseButton onClick={onClose} aria-label="Zamknij">
          &times;
        </CloseButton>
        <Title>{privacyPolicy.title}</Title>
        {privacyPolicy.sections.map((section) => (
          <Section key={section.id}>
            <SectionHeading>{section.heading}</SectionHeading>
            <SectionContent>
              {section.content.map((item, index) => (
                <ContentItem key={index}>{item}</ContentItem>
              ))}
            </SectionContent>
          </Section>
        ))}
      </ModalContainer>
    </Overlay>
  );
};

export default PrivacyPolicyModal;
